import {
  InteractiveContainer,
  InteractiveContainerProps,
} from "@jugl-web/ui-components/cross-platform/InteractiveContainer";
import { Tooltip } from "@jugl-web/ui-components/cross-platform/Tooltip";
import { cx, useTranslations } from "@jugl-web/utils";
import {
  CSSProperties,
  FC,
  forwardRef,
  HTMLAttributes,
  ReactNode,
} from "react";
import { ReactComponent as QuestionMarkIcon } from "./assets/question-mark.svg";
import { ReactComponent as ReorderIcon } from "./assets/reorder.svg";

interface ContainerProps {
  children: ReactNode;
}

const TABLE_BORDER_WIDTH = 1;

const Container: FC<ContainerProps> = ({ children }) => (
  <div
    className="overflow-auto rounded-xl border-solid border-[#EEF2F5] bg-[#EEF2F5]"
    style={{ borderWidth: TABLE_BORDER_WIDTH }}
  >
    {children}
  </div>
);

export interface RowProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  height: number;
  isHoverable?: boolean;
  isFirst?: boolean;
  isActive?: boolean;
  isDragging?: boolean;
  onClick?: InteractiveContainerProps["onClick"];
}

const Row = forwardRef<HTMLDivElement, RowProps>(
  (
    {
      children,
      height,
      isHoverable = true,
      isFirst = false,
      isActive = false,
      isDragging = false,
      className,
      style,
      onClick,
      ...props
    },
    ref
  ) => {
    const isInteractive = !!onClick;

    const baseProps = {
      ref,
      className: cx(
        "flex",
        isHoverable && "group",
        isDragging &&
          "border-grey-400 border border-solid shadow-[0px_4px_10px_0px_rgba(0,0,0,0.08)] rotate-[0.5deg]",
        className
      ),
      style: {
        height,
        gap: TABLE_BORDER_WIDTH,
        marginTop: !isFirst ? TABLE_BORDER_WIDTH : undefined,
        ...style,
      },
      children,
      "data-active": isActive,
      ...props,
    };

    if (isInteractive) {
      return <InteractiveContainer {...baseProps} onClick={onClick} />;
    }

    return <div {...baseProps} />;
  }
);

interface CellProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  width?: number;
  shouldGrow?: boolean;
  className?: string;
  style?: CSSProperties;
  onClick?: InteractiveContainerProps["onClick"];
}

const Cell = forwardRef<HTMLDivElement, CellProps>(
  (
    { children, width, shouldGrow, className, style, onClick, ...props },
    ref
  ) => {
    const isInteractive = !!onClick;

    const commonProps = {
      ref,
      className: cx(
        "flex shrink-0 items-center bg-white px-4 transition-colors group-hover:bg-grey-100 group-data-[active=true]:bg-grey-100",
        shouldGrow && "flex-grow",
        className
      ),
      style: { width, ...style },
      children,
      ...props,
    };

    if (isInteractive) {
      return <InteractiveContainer {...commonProps} onClick={onClick} />;
    }

    return <div {...commonProps} />;
  }
);

interface CellTextProps {
  children: ReactNode;
  className?: string;
}

const CellText: FC<CellTextProps> = ({ children, className }) => (
  <span
    className={cx(
      "font-secondary text-dark truncate text-sm font-normal leading-[150%]",
      className
    )}
  >
    {children}
  </span>
);

interface HeadingCellTextProps {
  children: ReactNode;
  className?: string;
}

const HeadingCellText: FC<HeadingCellTextProps> = ({ children, className }) => (
  <span
    className={cx(
      "text-dark-700 font-secondary truncate text-xs font-normal leading-[140%]",
      className
    )}
  >
    {children}
  </span>
);

interface TooltipedHintIconProps {
  hintText: string;
}

const QuestionMarkTooltipIcon: FC<TooltipedHintIconProps> = ({ hintText }) => (
  <Tooltip
    delay="none"
    renderTrigger={({ ref, props }) => (
      <QuestionMarkIcon ref={ref} {...props} />
    )}
    className="text-center"
  >
    {hintText}
  </Tooltip>
);

const EmptyState: FC = () => {
  const { t } = useTranslations();

  return (
    <div className="mt-[80px] text-center">
      <span className="font-secondary text-dark-700 text-sm">
        {t({
          id: "common.no-results",
          defaultMessage: "No results",
        }).concat(" 😔")}
      </span>
    </div>
  );
};

export const TaskFieldsTableLayout = {
  Container,
  Row,
  Cell,
  CellText,
  HeadingCellText,
  ReorderIcon,
  QuestionMarkTooltipIcon,
  EmptyState,
};
